import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { AUTH_CLEARED_EVENT, AUTH_LOGOUT_KEY, OAUTH_INTENT_KEY, acceptFreshAuth,
  authRevision, clearBrowserAuth, isLoginEntry, requiresFreshLogin, storedAuthTokens } from "../lib/authSession";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutRequest = useRef(null);

  const logout = useCallback(() => {
    if (logoutRequest.current) return logoutRequest.current;
    const tokens = storedAuthTokens();
    clearBrowserAuth();
    setUser(false);
    setLoading(false);
    logoutRequest.current = api.post("/auth/logout", { tokens }, { skipStoredAuth: true, timeout: 10000 })
      .then(() => true)
      .catch(() => false) // Stay locally signed out; fresh sign-in waits for successful server cleanup.
      .finally(() => { logoutRequest.current = null; });
    return logoutRequest.current;
  }, []);

  const refresh = useCallback(async () => {
    if (isLoginEntry() || requiresFreshLogin()) {
      setUser(false);
      setLoading(false);
      return;
    }
    const version = authRevision();
    try {
      const { data } = await api.get("/auth/me");
      if (version === authRevision() && !isLoginEntry() && !requiresFreshLogin()) setUser(data);
    } catch {
      if (version === authRevision()) setUser(false);
    } finally {
      if (version === authRevision()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const cleared = () => { setUser(false); setLoading(false); };
    const changed = (event) => { if (event.key === AUTH_LOGOUT_KEY) clearBrowserAuth(false); };
    const restored = (event) => {
      if (event.persisted && (requiresFreshLogin() || isLoginEntry())) logout();
    };
    window.addEventListener(AUTH_CLEARED_EVENT, cleared);
    window.addEventListener("storage", changed);
    window.addEventListener("pageshow", restored);
    return () => {
      window.removeEventListener(AUTH_CLEARED_EVENT, cleared);
      window.removeEventListener("storage", changed);
      window.removeEventListener("pageshow", restored);
    };
  }, [logout]);

  useEffect(() => {
    if (isLoginEntry(location.pathname)) {
      logout();
    } else if (window.location.hash?.includes("session_id=")) {
      setLoading(false); // Only the explicitly initiated callback may establish this session.
    } else {
      refresh();
    }
  }, [location.pathname, logout, refresh]);

  const acceptResult = useCallback(async (data, version, clientSession = false) => {
    if (version !== authRevision()) {
      // A late response can set HttpOnly cookies even when its React update is ignored.
      await api.post("/auth/logout", { tokens: data.access_token ? [data.access_token] : [] }, { skipStoredAuth: true });
      throw new Error("Sign-in was canceled. Please sign in again.");
    }
    acceptFreshAuth(data, clientSession);
    setUser(clientSession ? false : data);
    setLoading(false);
    return data;
  }, []);

  const authenticate = useCallback(async (endpoint, payload, clientSession = false) => {
    if (!await logout()) throw new Error("Could not reset your session. Please try signing in again.");
    const version = authRevision();
    const { data } = await api.post(endpoint, payload, { skipStoredAuth: true });
    return acceptResult(data, version, clientSession);
  }, [acceptResult, logout]);

  const login = (email, password) => authenticate("/auth/login", { email, password });
  const register = (payload) => authenticate("/auth/register", payload);
  const loginClient = (payload) => authenticate("/auth/client/otp-login", payload, true);

  const continueWithEmailPhone = async (payload) => {
    if (!await logout()) throw new Error("Could not reset your session. Please try signing in again.");
    const version = authRevision();
    const { data } = await api.post("/personnel/apply", payload, { skipStoredAuth: true });
    if (data.access_token) return acceptResult(data, version);
    if (version !== authRevision()) throw new Error("Sign-in was canceled. Please try again.");
    return data; // New applicants remain signed out while awaiting approval.
  };

  const startGoogleLogin = async () => {
    if (!await logout()) throw new Error("Could not reset your session. Please try signing in again.");
    const version = authRevision();
    const { data } = await api.post("/auth/session/start", {}, { skipStoredAuth: true });
    if (version !== authRevision()) throw new Error("Sign-in was canceled. Please try again.");
    sessionStorage.setItem(OAUTH_INTENT_KEY, data.login_intent);
  };

  const completeGoogleLogin = useCallback(async (sessionId) => {
    const intent = sessionStorage.getItem(OAUTH_INTENT_KEY);
    sessionStorage.removeItem(OAUTH_INTENT_KEY);
    if (!intent) throw new Error("Start a new sign-in from the login page.");
    const version = authRevision();
    const { data } = await api.post("/auth/session", { session_id: sessionId, login_intent: intent }, { skipStoredAuth: true });
    return acceptResult(data, version);
  }, [acceptResult]);

  return (
    <AuthContext.Provider value={{ user: isLoginEntry(location.pathname) ? false : user,
      loading, login, register, loginClient, continueWithEmailPhone, logout, refresh, startGoogleLogin, completeGoogleLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);